import cn from 'classnames';
import { FC, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useAppSelector } from '@/shared/hooks';
import { Icon } from '@/shared/ui';

import { billingModel } from '@/entities/billing';

import styles from './PaymentReminder.module.scss';

type PaymentReminderProps = {
  className?: string;
  testID?: string;
};

export const PaymentReminder: FC<PaymentReminderProps> = (props) => {
  const { className, testID } = props;

  const { formatMessage, formatDate } = useIntl();

  const paymentReminderData = useAppSelector(
    billingModel.selectors.selectPaymentReminderData,
  );

  const {
    activeSubscriptions,
    hasUnpaidSubscription,
    hasMultipleActiveSubscriptions,
    nextSubscriptionStart,
    currentSubscriptionEnd,
  } = paymentReminderData;

  const shouldShowReminder =
    hasMultipleActiveSubscriptions || hasUnpaidSubscription;

  const reminderMessage = useMemo(() => {
    if (hasUnpaidSubscription && activeSubscriptions.length === 1) {
      return {
        type: 'warning' as const,
        text: formatMessage(
          {
            id: 'billing.paymentReminder.waitingPayment',
            defaultMessage:
              'Payment pending for next subscription period. Subscription active until {dueDate}',
          },
          {
            dueDate: currentSubscriptionEnd
              ? formatDate(currentSubscriptionEnd, {
                  day: 'numeric',
                  month: 'numeric',
                  year: 'numeric',
                })
              : '',
          },
        ),
      };
    }

    if (hasMultipleActiveSubscriptions) {
      return {
        type: 'success' as const,
        text: formatMessage(
          {
            id: 'billing.paymentReminder.paid',
            defaultMessage:
              "You've successfully paid for the next subscription period starting on {startDate}",
          },
          {
            startDate: nextSubscriptionStart
              ? formatDate(nextSubscriptionStart, {
                  day: 'numeric',
                  month: 'numeric',
                  year: 'numeric',
                })
              : '',
          },
        ),
      };
    }

    return null;
  }, [
    hasUnpaidSubscription,
    activeSubscriptions,
    hasMultipleActiveSubscriptions,
    formatMessage,
    formatDate,
    currentSubscriptionEnd,
    nextSubscriptionStart,
  ]);

  if (!shouldShowReminder || !reminderMessage) {
    return null;
  }

  return (
    <div
      data-testid={testID}
      data-component="payment-reminder"
      className={cn(
        styles.container,
        className,
        reminderMessage.type === 'warning' && styles.warning,
      )}
    >
      <Icon
        name={reminderMessage.type === 'warning' ? 'attention' : 'check'}
        size={24}
      />

      <span className="p3">{reminderMessage.text}</span>
    </div>
  );
};
